import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { formatRoute } from 'react-router-named-routes';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Icon from '@mdi/react';
import {
  mdiCheckOutline,
  mdiSwapHorizontalCircle,
  mdiUndoVariant,
} from '@mdi/js';
import { useDispatch } from 'react-redux';
import DeleteIcon from '@mui/icons-material/Delete';
import imgPlaceholder from '../../assets/images/imgPlaceholder.png';
import MealTypesChips from '../meal/meal_types_chips';
import {
  END_USER_FIND_ALTERNATIVES_ROUTE,
  MY_DAY_MEAL_ROUTE,
} from '../../services/routes';
import { linkWithQueryParams } from '../../services/urls';
import DeleteCardConfirmPopover from '../common/delete_card_confirm_popover';
import {
  dayIndexForCalendarDate,
  dbWeekStartDateForViewWeekAndIndex,
  plannerWeekWithObjectRemoved,
  viewWeekStartDateStr,
} from '../../services/planner';
import {
  useAddUserMyDayActionRecordMutation,
  useDeleteMealMutation,
  useGetDbPlannerWeekQuery,
  useGetMyDayActionRecordsByEnrollmentQuery,
  useGetMyDayActionRecordsBySpaceMembershipQuery,
  useUpdateDbPlannerWeekOptimisticallyMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  isMealCompleted,
  isMealSkipped,
  mealCompletedActionRecord,
  mealSkippedActionRecord,
  mealUnskippedActionRecord,
} from '../../services/my_day';
import { mealsRemovedFromMealBasket } from '../../features/end_user_shopping/end_user_shopping_slice';
import { mergeMyDayActionRecords } from '../../services/programmes';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetCurrentSpaceMembershipQuery,
  useGetDistilledMealByIDAndParentQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import MyDayPlaceholderCard from './my_day_placeholder_card';

const MyDayCompactMealCard = ({ mealID, parentID, calendarDay }) => {
  // const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: distilledMeal, isSuccess: isMealSuccess } =
    useGetDistilledMealByIDAndParentQuery({ id: mealID, parentID });

  const title = distilledMeal?.recipes[0]?.title;
  const mealType = ((distilledMeal?.recipes || [])[0]?.mealTypes || [])[0];

  const { isSuccess: isUserProfileSuccess } = useGetUserProfileQuery();

  const plannerViewWeekStartDay = 1; /* plannerViewWeekStartDayOrDefault(
    userProfile?.plannerViewWeekStartDay,
  ); */

  const plannerViewWeekStartDate = viewWeekStartDateStr(
    calendarDay,
    plannerViewWeekStartDay,
  );

  const dayIndex = dayIndexForCalendarDate(calendarDay);

  const plannerDbWeekStartDate = dbWeekStartDateForViewWeekAndIndex(
    plannerViewWeekStartDate,
    dayIndex,
  );
  const { data: dbPlannerWeek, isSuccess: isDbPlannerWeekSuccess } =
    useGetDbPlannerWeekQuery(plannerDbWeekStartDate);

  const [deleteMealAnchorEl, setDeleteMealAnchorEl] = useState(null);

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekOptimisticallyMutation();

  const {
    data: currentProgrammeEnrollment,
    isLoading: enrollmentLoading,
    isSuccess: isEnrollmentSuccess,
  } = useCurrentProgrammeEnrollmentQuery();

  const { data: currentSpaceMembership, isSuccess: isSpaceMembershipSuccess } =
    useGetCurrentSpaceMembershipQuery();

  const {
    data: myDayActionRecordsByEnrollment,
    isLoading: isMyDayActionRecordsByEnrollmentLoading,
    isSuccess: isMyDayActionRecordsByEnrollmentSuccess,
  } = useGetMyDayActionRecordsByEnrollmentQuery(
    currentProgrammeEnrollment?.id,
    {
      skip: !currentProgrammeEnrollment?.id,
    },
  );

  const {
    data: myDayActionRecordsBySpaceMembership,
    isLoading: isMyDayActionRecordsBySpaceMembershipLoading,
    isSuccess: isMyDayActionRecordsBySpaceMembershipSuccess,
  } = useGetMyDayActionRecordsBySpaceMembershipQuery(
    currentSpaceMembership?.id,
    {
      skip: !currentSpaceMembership?.id,
    },
  );

  const hasLoadedSuccessfully =
    isMealSuccess &&
    isSpaceMembershipSuccess &&
    isEnrollmentSuccess &&
    (!currentProgrammeEnrollment || isMyDayActionRecordsByEnrollmentSuccess) &&
    (!currentProgrammeEnrollment ||
      isMyDayActionRecordsBySpaceMembershipSuccess) &&
    isDbPlannerWeekSuccess &&
    isUserProfileSuccess;

  const cardActionsAvailable =
    !enrollmentLoading &&
    !isMyDayActionRecordsByEnrollmentLoading &&
    !isMyDayActionRecordsBySpaceMembershipLoading;

  const myDayActionRecords = mergeMyDayActionRecords(
    myDayActionRecordsBySpaceMembership || [],
    myDayActionRecordsByEnrollment || [],
  );

  const [addUserMyDayActionRecord] = useAddUserMyDayActionRecordMutation();

  const [deleteMeal] = useDeleteMealMutation();

  const isCompleted = isMealCompleted(
    mealID,
    currentProgrammeEnrollment?.id,
    myDayActionRecords || [],
  );

  const isSkipped = isMealSkipped(
    mealID,
    currentProgrammeEnrollment?.id,
    myDayActionRecords || [],
  );

  const onClickSkip = () => {
    addUserMyDayActionRecord(
      mealSkippedActionRecord(
        currentSpaceMembership?.id,
        currentProgrammeEnrollment?.id,
        mealID,
        parentID,
        calendarDay,
      ),
    );
  };

  const onClickUnskip = () => {
    addUserMyDayActionRecord(
      mealUnskippedActionRecord(
        currentSpaceMembership?.id,
        currentProgrammeEnrollment?.id,
        mealID,
        parentID,
        calendarDay,
      ),
    );
  };

  const onClickComplete = () => {
    addUserMyDayActionRecord(
      mealCompletedActionRecord(
        currentSpaceMembership?.id,
        currentProgrammeEnrollment?.id,
        mealID,
        parentID,
        calendarDay,
      ),
    );
  };

  const onClickDelete = useCallback((ev) => {
    setDeleteMealAnchorEl(ev.target);
    ev.stopPropagation();
  }, []);

  const onDelete = useCallback(async () => {
    setDeleteMealAnchorEl(null);
    dispatch(mealsRemovedFromMealBasket([mealID]));
    const updatedDbPlannerWeek = plannerWeekWithObjectRemoved(
      dbPlannerWeek,
      mealID,
      dayIndex,
    );
    await Promise.all([
      deleteMeal({
        id: mealID,
        parentID,
      }),
      updateDbPlannerWeek(updatedDbPlannerWeek),
    ]);
  }, [
    dispatch,
    mealID,
    deleteMeal,
    parentID,
    dbPlannerWeek,
    dayIndex,
    updateDbPlannerWeek,
  ]);

  if (!hasLoadedSuccessfully) {
    return <MyDayPlaceholderCard />;
  }

  if (hasLoadedSuccessfully && !distilledMeal) {
    return null;
  }

  return (
    <>
      <Card
        className="my-day-compact-card"
        onClick={() => {
          navigate(
            formatRoute(MY_DAY_MEAL_ROUTE, {
              parentID,
              mealID,
            }),
          );
        }}>
        <CardMedia
          sx={{ height: 160 }}
          image={distilledMeal.recipes[0].imageUrl || imgPlaceholder}
        />
        <CardContent
          style={{ padding: '12px 12px', borderBottom: '1px solid #7B7B7B' }}>
          <Typography
            variant="body2"
            component="p"
            sx={{ flexGrow: 1 }}
            style={{
              lineClamp: 1,
              WebkitLineClamp: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
            }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex' }}>
            <MealTypesChips mealTypes={distilledMeal.recipes[0].mealTypes} />
          </Box>
        </CardContent>
        <CardActions
          sx={{ justifyContent: 'space-around' }}
          style={{ paddingTop: '0' }}>
          {cardActionsAvailable && (
            <>
              {isCompleted && (
                <div
                  className="myDayActionCardCompleted"
                  style={{ paddingTop: '8px' }}>
                  COMPLETED - GOOD JOB!
                </div>
              )}
              {currentProgrammeEnrollment && !isCompleted && isSkipped && (
                <Tooltip title="Unskip this meal" placement="top-start">
                  <IconButton
                    aria-label="unskip"
                    size="small"
                    onClick={(ev) => {
                      onClickUnskip();
                      ev.stopPropagation();
                    }}>
                    <Icon
                      path={mdiUndoVariant}
                      style={{ width: '32px', height: '32px' }}
                    />
                  </IconButton>
                </Tooltip>
              )}
              {!isCompleted && !isSkipped && (
                <Tooltip title="Find alternatives" placement="top-start">
                  <IconButton
                    aria-label="find alternatives"
                    size="small"
                    disabled={isSkipped}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      navigate(
                        linkWithQueryParams(
                          formatRoute(END_USER_FIND_ALTERNATIVES_ROUTE, {
                            calendarDay,
                          }),
                          { mealType, mealId: distilledMeal?.id },
                        ),
                      );
                    }}>
                    <Icon
                      path={mdiSwapHorizontalCircle}
                      style={{ width: '32px', height: '32px' }}
                    />
                  </IconButton>
                </Tooltip>
              )}
              {currentProgrammeEnrollment && !isCompleted && !isSkipped && (
                <>
                  <Tooltip title="Complete this meal" placement="top-start">
                    <IconButton
                      aria-label="complete"
                      size="small"
                      disabled={isSkipped}
                      onClick={(ev) => {
                        onClickComplete();
                        ev.stopPropagation();
                      }}>
                      <Icon
                        path={mdiCheckOutline}
                        style={{ width: '24px', height: '24px' }}
                      />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Skip this meal" placement="top-start">
                    <IconButton
                      aria-label="skip"
                      size="small"
                      style={{ width: '32px', height: '32px' }}
                      onClick={(ev) => {
                        onClickSkip();
                        ev.stopPropagation();
                      }}>
                      <DeleteIcon style={{ width: '32px', height: '32px' }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {!currentProgrammeEnrollment && (
                <Tooltip title="Delete this meal" placement="top-start">
                  <IconButton
                    aria-label="delete"
                    size="small"
                    style={{ width: '32px', height: '32px' }}
                    onClick={(ev) => {
                      onClickDelete(ev);
                      ev.stopPropagation();
                    }}>
                    <DeleteIcon style={{ width: '32px', height: '32px' }} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </CardActions>
      </Card>
      <DeleteCardConfirmPopover
        visible={!!deleteMealAnchorEl}
        anchorEl={deleteMealAnchorEl}
        title="Delete meal"
        message="The meal will be deleted permanently. Are you sure?"
        height={140}
        onConfirmDelete={onDelete}
        onDismiss={() => setDeleteMealAnchorEl(null)}
      />
    </>
  );
};

MyDayCompactMealCard.propTypes = {
  mealID: PropTypes.string.isRequired,
  parentID: PropTypes.string.isRequired,
  calendarDay: PropTypes.string.isRequired,
};

export default MyDayCompactMealCard;
